import { useTheme } from "@mui/material";
import pluralize from "pluralize";

import { Divider } from "@/Components/v2/atoms/Divider";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { Select } from "@/Components/v2/atoms/Select";
import { Stack } from "@/Components/v2/atoms/Stack";
import { BoldSpan, Typography } from "@/Components/v2/atoms/Typography";
import ChartSettings from "@/shared/components/Chart/ChartSettings";
import ROIChart from "@/shared/components/Chart/ROIChart";
import { BreakdownDataProps } from "@/shared/components/Sections/BreakdownChart/BreakdownChart";
import Welcome from "@/shared/components/Sections/Welcome";
import {
  ChannelData,
  ChannelDataWithSourceAndTotal,
  ROIBreakdownEnum,
  ViewToggle,
  YAxisClickParams,
} from "@/Utils/sharedTypes";
import { useConfig, Config } from "@/hooks/useConfig";

import { useGeographiesStore } from "../../store/useGeographiesStore";

pluralize.addIrregularRule("geographic", "geographies");

interface BreakdownProps {
  data: ChannelDataWithSourceAndTotal[];
  title: string;
  showWelcomeScreen: boolean;
  totalSpend: number;
  totalRevenue: number;
  currencySymbol: string;
}

const BreakdownV2: React.FC<BreakdownProps> = ({
  data,
  title,
  showWelcomeScreen,
  totalSpend,
  totalRevenue,
  currencySymbol,
}) => {
  const currency = currencySymbol;
  const theme = useTheme();
  const { config } = useConfig();
  const {
    setChartView,
    setChartBreakdown,
    setIsAscending,
    setFilterInsufficientData,
    setGeographies,
    setChannels,
  } = useGeographiesStore();
  const { chartView, chartBreakdown, isAscending, filterInsufficientData } = useGeographiesStore(
    (state) => state.chart
  );

  const handleYAxisClick = (params: YAxisClickParams, mappedData: BreakdownDataProps[]) => {
    if (params.componentType === "yAxis" && params.targetType === "axisLabel") {
      const clickedLabel = params.value;
      const selectedItem = mappedData.find((item) => item.mappedName === clickedLabel);

      if (selectedItem) {
        if (chartView === ViewToggle.CHANNEL) {
          setChannels([selectedItem.id]);
          setChartView(ViewToggle.INSIGHT);
        } else {
          setChartView(ViewToggle.CHANNEL);
          setGeographies([selectedItem.id]);
        }
      }
    }
  };

  return (
    <Stack
      border={`1px solid ${theme.palette.section.border}`}
      borderRadius={4}
      marginY={2}
      padding={4}
      paddingY={3}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          ROI Breakdown
        </Typography>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <FormControl sx={{ width: "11rem" }}>
            <Select
              variant="standard"
              id="chartView"
              name="chartView"
              onChange={(e) => {
                setChartView(e.target.value as ViewToggle);
              }}
              value={chartView}
              renderValue={(value) => (
                <Typography variant="body2">
                  <BoldSpan>View: </BoldSpan>
                  {getViewDisplayText(value as ViewToggle, title)}
                </Typography>
              )}
            >
              <MenuItem value={ViewToggle.INSIGHT}>{pluralize(title)}</MenuItem>
              <MenuItem value={ViewToggle.CHANNEL}>Channels</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: "15rem" }}>
            <Select
              variant="standard"
              id="chartBreakdown"
              name="chartBreakdown"
              onChange={(e) => {
                setChartBreakdown(e.target.value as ROIBreakdownEnum);
              }}
              value={chartBreakdown}
              renderValue={(value) => (
                <Typography variant="body2">
                  <BoldSpan>Show: </BoldSpan>
                  {getBreakdownDisplayText(value as ROIBreakdownEnum, currency, config)}
                </Typography>
              )}
            >
              <MenuItem value={ROIBreakdownEnum.SPEND_DOLLARS}>
                {getBreakdownDisplayText(ROIBreakdownEnum.SPEND_DOLLARS, currency, config)}
              </MenuItem>
              <MenuItem value={ROIBreakdownEnum.SPEND_PERCENTAGE}>
                {getBreakdownDisplayText(ROIBreakdownEnum.SPEND_PERCENTAGE, currency, config)}
              </MenuItem>
              <MenuItem value={ROIBreakdownEnum.ROI}>
                {getBreakdownDisplayText(ROIBreakdownEnum.ROI, currency, config)}
              </MenuItem>
            </Select>
          </FormControl>
          <ChartSettings
            isAscending={isAscending}
            setIsAscending={setIsAscending}
            filterInsufficientData={filterInsufficientData}
            setFilterInsufficientData={setFilterInsufficientData}
          />
        </Stack>
      </Stack>
      <Divider />
      <Stack marginTop={4}>
        {showWelcomeScreen ? (
          <Welcome title={title} />
        ) : (
          <ROIChart
            totalSpend={totalSpend}
            totalRevenue={totalRevenue}
            channelROI={data as unknown as Record<string, ChannelData>}
            insightROI={data as unknown as Record<string, ChannelData>}
            chartView={chartView}
            chartLevel={chartBreakdown}
            isAscending={isAscending}
            filterInsufficientData={filterInsufficientData}
            onYAxisClick={handleYAxisClick}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default BreakdownV2;

const getBreakdownDisplayText = (value: ROIBreakdownEnum, currency: string, config: Config) => {
  const { showReturnInsteadOfRevenue, revenueLabel } = config;
  switch (value) {
    case ROIBreakdownEnum.SPEND_DOLLARS:
      return `Spend vs ${revenueLabel} ${showReturnInsteadOfRevenue ? "" : `(${currency})`}`;
    case ROIBreakdownEnum.SPEND_PERCENTAGE:
      return `Spend vs ${revenueLabel} (%)`;
    case ROIBreakdownEnum.ROI:
      return "ROI (%)";
    default:
      return value;
  }
};

const getViewDisplayText = (value: ViewToggle, title: string) => {
  switch (value) {
    case ViewToggle.CHANNEL:
      return "Channels";
    case ViewToggle.INSIGHT:
      return pluralize(title);
    default:
      return title;
  }
};
